import { privateFetch, publicFetch } from "../libs/axios"
import { jsonToQueryString } from "../utils/Common"

import type {
  GiftCardValidityRequestType,
  GiftCardValidityResponseType,
} from "../types/GiftCardType"
import type {
  CreateDynamicAssetsRequestType,
  CreateProductClosureType,
  CreateProductRequestType,
  CreateProductTimeSlotRequestType,
  EditProductInfoRequestType,
  GetAllOffersInGiftcardResponseType,
  GetAllProductResponseType,
  GetGiftcardOfferUsageDetailsResponseType,
  GetProductAddonsResponseType,
  ProductAgreementsRequestType,
  ProductAvailabilityResponseType,
  ProductClosureResponseType,
  ProductClosureType,
  ProductInfoTnCRequestType,
  ProductLocationFormAttributes,
  ProductLocationResponseType,
  ProductTypeRequestType,
  ProductTypeTypes,
  ProductUniqueCodeResponseType,
} from "../types/ProductTypes"
import type { AxiosRequestConfig } from "axios"

export const productCategories = async (body: any) =>
  await privateFetch().get("/api/v1/categories?filter[type]=product", body)

// PRODUCT TYPE
export const getProductType = async (config: AxiosRequestConfig = {}) =>
  await privateFetch().get<ProductTypeRequestType>("/api/v1/product/type?paginate=0", config)

export const getProductTypeById = async (id: string, config: AxiosRequestConfig = {}) =>
  await privateFetch().get(`/api/v1/product/type/${id}`, config)

export const addProductType = async (body: CreateProductRequestType) =>
  await privateFetch().post<ProductTypeTypes>("/api/v1/product/type", body)

// PRODUCT INFO API
export const checkUniqueName = async (field: string, value: string, product_id: string) =>
  await privateFetch().get<ProductUniqueCodeResponseType>(
    `/api/v1/product/field/unique?field=${field}&value=${encodeURIComponent(value)}${product_id ? `&product_id=${product_id}` : ""}`
  )

export const storeProductInfo = async (body: FormData, headers: AxiosRequestConfig) =>
  await privateFetch().post("/api/v1/product/", body, headers)

export const updateProductInfoById = async (
  id: string,
  body: FormData,
  headers?: AxiosRequestConfig
) => await privateFetch().post(`/api/v1/product/${id}`, body, headers)

export const updateProductInfoTnC = async (id: string, body: ProductInfoTnCRequestType) =>
  await privateFetch().put(`/api/v1/product/${id}`, body)

export const getProductInfoById = async (
  id: string,
  config: AxiosRequestConfig = {},
  isIntegratedPage = false
) => {
  if (isIntegratedPage) {
    return await publicFetch().get(`/api/v1/public/product/${id}`, config)
  }
  return await privateFetch().get<EditProductInfoRequestType>(`/api/v1/product/${id}`, config)
}

export const addProductAgreements = async (id: string, body: ProductAgreementsRequestType[]) => {
  await privateFetch().post(`api/v1/product/${id}/agreements`, body)
}

export const getProductAgreementsList = async (
  id: string,
  isIntegratedPage?: boolean,
  config: AxiosRequestConfig = {}
) => {
  if (isIntegratedPage) {
    return await publicFetch().get(`/api/v1/public/product/${id}/agreements`, config)
  }
  return await privateFetch().get(`/api/v1/product/${id}/agreements`, config)
}

export const updateProductAgreementList = async (
  id: string,
  body: ProductAgreementsRequestType[]
) => await privateFetch().put(`/api/v1/product/${id}/agreements`, body)

export const deleteProductAgreement = async (id: string, agreementId: string) =>
  await privateFetch().delete(`/api/v1/product/${id}/agreements/${agreementId}`)

// STORE PRODUCT LOCATIONS
export const storeProductLocationById = async (id: string, body: ProductLocationFormAttributes) =>
  await privateFetch().post(`/api/v1/product/${id}/location`, body)

export const storeProductLocationByProdIdAndLocationId = async (
  id: string,
  locationId: string,
  body: ProductLocationFormAttributes
) => await privateFetch().put(`/api/v1/product/${id}/location/${locationId}`, body)

export const getProductLocationById = async (id: string, config: AxiosRequestConfig = {}) =>
  await privateFetch().get<ProductLocationResponseType>(`/api/v1/product/${id}/location`, config)

/* PRODUCT INVENTORY */

export const getProductInventory = async (id: string, config: AxiosRequestConfig = {}) =>
  await privateFetch().get(`/api/v1/product/${id}/product-inventory`, config)

export const addProductInventory = async (id: string, body: CreateDynamicAssetsRequestType) =>
  await privateFetch().post(`/api/v1/product/${id}/product-inventory`, body)

export const editProductInventory = async (
  id: string,
  inventoryId: string,
  body: CreateDynamicAssetsRequestType
) => await privateFetch().put(`/api/v1/product/${id}/product-inventory/${inventoryId}`, body)

/* PRODUCT SCHEDULING */

export const getProductAvailability = async (id: string, config: AxiosRequestConfig = {}) =>
  await privateFetch().get<ProductAvailabilityResponseType>(
    `/api/v1/product/${id}/availability`,
    config
  )

export const getProductAvailabilityById = async (
  id: string,
  schedulingId: string,
  config: AxiosRequestConfig = {}
) => await privateFetch().get(`/api/v1/product/${id}/availability/${schedulingId}`, config)

export const addProductAvailability = async (id: string, body: CreateProductTimeSlotRequestType) =>
  await privateFetch().post(`/api/v1/product/${id}/availability`, body)

export const editProductAvailability = async (
  id: string,
  schedulingId: string,
  body: CreateProductTimeSlotRequestType
) => await privateFetch().put(`/api/v1/product/${id}/availability/${schedulingId}`, body)

// RENTAL PRODUCT SCHEDULING

export const addRentalProductAvailability = async (
  id: string,
  body: CreateProductTimeSlotRequestType
) => await privateFetch().post(`/api/v1/product/${id}/availability/rentals`, body)

export const editRentalProductAvailability = async (
  id: string,
  schedulingId: string,
  body: CreateProductTimeSlotRequestType
) => await privateFetch().put(`/api/v1/product/${id}/availability/rentals/${schedulingId}`, body)

export const getRentalProductAvailabilityById = async (
  id: string,
  schedulingId: string,
  config: AxiosRequestConfig = {}
) => await privateFetch().get(`/api/v1/product/${id}/availability/rentals/${schedulingId}`, config)

// GET ALL PRODUCTS LIST
export const getAllProducts = async (filter = {}, config: AxiosRequestConfig = {}) =>
  await privateFetch().get<GetAllProductResponseType>(
    `/api/v1/product/${jsonToQueryString(filter)}`,
    config
  )

export const deleteProduct = async (id: string) =>
  await privateFetch().delete(`/api/v1/product/${id}`)

// PRODUCT CLOSURE API
export const getProductClosures = async (id: string, config: AxiosRequestConfig = {}) =>
  await privateFetch().get<ProductClosureResponseType>(
    `/api/v1/product/${id}/product-closure`,
    config
  )

export const getProductClosuresById = async (id: string, closureId: string) =>
  await privateFetch().get<ProductClosureType>(`/api/v1/product/${id}/product-closure/${closureId}`)

export const deleteProductAvailabilitySlots = async (
  productId: string,
  productAvailabilityId: string,
  filter = {}
) =>
  await privateFetch().delete(
    `/api/v1/product/${productId}/availability/${productAvailabilityId}${jsonToQueryString(filter)}`
  )

export const addProductClosures = async (id: string, body: CreateProductClosureType) =>
  await privateFetch().post(`/api/v1/product/${id}/product-closure`, body)

export const editProductClosures = async (
  id: string,
  closureId: string,
  body: CreateProductClosureType
) => await privateFetch().put(`/api/v1/product/${id}/product-closure/${closureId}`, body)

export const deleteProductClosure = async (id: string, pClosureId: string) =>
  await privateFetch().delete(`/api/v1/product/${id}/product-closure/${pClosureId}`)

// GIFT CARD VALIDITY
export const createGiftCardValidity = async (id: string, body: GiftCardValidityRequestType) =>
  await privateFetch().post(`/api/v1/product/${id}/validity`, body)

export const getGiftCardValidity = async (
  id: string,
  config: AxiosRequestConfig = {},
  isIntegratedPage = false
) => {
  if (isIntegratedPage) {
    return await publicFetch().get<GiftCardValidityResponseType>(
      `/api/v1/public/product/${id}/validity`,
      config
    )
  }
  return await privateFetch().get<GiftCardValidityResponseType>(
    `/api/v1/product/${id}/validity`,
    config
  )
}

// PRODUCT PRICING SHEET

export const getProductPriceSheet = async (id: string, config: AxiosRequestConfig = {}) =>
  await privateFetch().get(`/api/v1/product/${id}/price/sheet?include=priceSeason`, config)

export const getProductBookingFields = async (id: string, config: AxiosRequestConfig = {}) =>
  await privateFetch().get(`/api/v1/product/${id}/fields`, config)

export const deleteProductMedia = async (
  id: string,
  mediauId: string,
  config: AxiosRequestConfig = {}
) => await privateFetch().delete(`/api/v1/product/${id}/media/${mediauId}`, config)

// Associate a units with availibility for rentals

export const AssoiciatedUnitsWithAvalibility = async (
  productId: string,
  availibilityId: string,
  body: any
) =>
  await privateFetch().put(
    `/api/v1/product/${productId}/availability/${availibilityId}/units`,
    body
  )

export const getUnitsBasedOnAvailibilityId = async (productId: string, availibilityId: string) =>
  await privateFetch().get(`/api/v1/product/${productId}/availability/${availibilityId}/units`)

// attach domain location to the product

export const AttachLocationToProduct = async (productId: string, body: any) =>
  await privateFetch().post(`/api/v1/domain-location/assign/${productId}`, body)

// get domain location of product

export const getAttachedLocationOfProduct = async (
  productId: string,
  isIntegratedPage?: boolean
) => {
  if (isIntegratedPage) {
    return await publicFetch().get(`/api/v1/public/domain-location/product/${productId}`)
  }
  return await privateFetch().get(`/api/v1/domain-location/product/${productId}`)
}

export const detachhedLocationToProduct = async (productId: string, body: any) =>
  await privateFetch().post(`/api/v1/domain-location/detach/${productId}`, body)

// Attach a add-ons product to product

export const createAddOnProduct = async (productId: string, body: any) =>
  await privateFetch().post(`/api/v1/product/${productId}/add-on`, body)

export const getProductAddons = async (
  productId: string,
  isIntegratedPage = false,
  filter = {}
) => {
  if (isIntegratedPage) {
    return await publicFetch().get<GetProductAddonsResponseType>(
      `/api/v1/public/product/${productId}/add-ons${jsonToQueryString(filter)}`
    )
  }
  return await privateFetch().get<GetProductAddonsResponseType>(
    `/api/v1/product/${productId}/add-ons${jsonToQueryString(filter)}`
  )
}

export const deleteAddonProduct = async (productId: string, body: any) => {
  await privateFetch().post(`api/v1/product/${productId}/add-on/delete`, body)
}

export const editAddonProduct = async (productId: string, body: any) =>
  await privateFetch().put(`/api/v1/product/${productId}`, body)

export const getDynamicRentalUnitTypes = async (productId: string, isIntegratedPage: boolean) => {
  if (isIntegratedPage) {
    return await publicFetch().get(`/api/v1/public/product/${productId}/rentals/unit-types`)
  }
  return await privateFetch().get(`/api/v1/product/${productId}/rentals/unit-types`)
}

export const getDynamicRentalsUnitsBasedOnStartDate = async (
  productId: string,
  isIntegratedPage: boolean,
  filter = {}
) => {
  if (isIntegratedPage) {
    return await publicFetch().get(
      `/api/v1/public/product/${productId}/availability/rentals/start-date/${jsonToQueryString(filter)}`
    )
  }
  return await privateFetch().get(
    `/api/v1/product/${productId}/availability/rentals/start-date/${jsonToQueryString(filter)}`
  )
}

//  BULK-PRODUCT CLOSURE SERVICES
export const addBulkProductClosures = async (body: any) =>
  await privateFetch().post(`/api/v1/product/bulk/closures`, body)

export const getListBulkProductClosures = async (filter = {}) =>
  await privateFetch().get(`/api/v1/product/bulk/closures/${jsonToQueryString(filter)}`)

export const getBulkClosureById = async (closureId: string) =>
  await privateFetch().get(`/api/v1/product/bulk/closures/${closureId}`)

export const updateBulkProductClosures = async (closureId: string, body: any) =>
  await privateFetch().put(`/api/v1/product/bulk/closures/${closureId}`, body)

export const deleteProductBulkClosureById = async (closureId: string) =>
  await privateFetch().delete(`/api/v1/product/bulk/closures/${closureId}`)

export const getSimilarUnits = async (productId: string, filter = {}) =>
  await privateFetch().get(
    `/api/v1/product/${productId}/availability/similar-units/${jsonToQueryString(filter)}`
  )

export const getGiftcardOfferUsageDetails = async (offerId: string) =>
  await privateFetch().get<GetGiftcardOfferUsageDetailsResponseType>(
    `/api/v1/giftcard/${offerId}/usages`
  )

export const getAllGiftcardOffers = async (filter = {}) =>
  await privateFetch().get<GetAllOffersInGiftcardResponseType>(
    `/api/v1/giftcards/${jsonToQueryString(filter)}`
  )

// product getbase64url
export const getBase64imageUrl = async (body: any) =>
  await privateFetch().post(`/api/v1/image-to-base64`, body)

export const reOrderMedia = async (productId: string, body: any) =>
  await privateFetch().post(`/api/v1/product/${productId}/reorder-media`, body)
